import { Button, Stack } from '@mantine/core';
import { useState, useRef, useEffect } from 'react';
import { IconEraser } from '@tabler/icons-react';
import { useIntl } from 'react-intl';

export const Signature = ({
  value,
  onChange,
  isDisabled,
}: {
  value?: string;
  onChange?: (signaturePath: string) => void;
  isDisabled?: boolean;
}) => {
  const intl = useIntl();
  const [signaturePath, setSignaturePath] = useState(value || '');
  const [isDown, setIsDown] = useState(false);
  const rectRef = useRef<SVGRectElement>(null);
  const isDownRef = useRef(false);
  const signaturePathRef = useRef('');
  const isDisabledRef = useRef(isDisabled);

  const isTouchEvent = (e: any) => (e.type.match(/^touch/) ? true : false);

  const getCoords = (e: any) => {
    if (isTouchEvent(e)) {
      // Use the target element instead of currentTarget
      const target = e.target || e.currentTarget;
      if (!target) {
        return '0,0';
      }

      // Check if targetTouches exists and has items
      // For touchEnd events, use changedTouches instead
      const touches = e.targetTouches || e.changedTouches;
      if (!touches || touches.length === 0) {
        return '0,0';
      }

      const rect = target.getBoundingClientRect();
      const x = touches[0].clientX - rect.left;
      const y = touches[0].clientY - rect.top;
      return `${x},${y}`;
    }

    // For mouse events, check if nativeEvent exists
    if (!e.nativeEvent) {
      return '0,0';
    }

    const x = e.nativeEvent.offsetX ?? 0;
    const y = e.nativeEvent.offsetY ?? 0;
    return `${x},${y}`;
  };

  const down = (e: any) => {
    // Prevent drawing if disabled
    if (isDisabledRef.current) {
      return;
    }
    setSignaturePath((prevPath) => {
      const newPath = `${prevPath}M${getCoords(e)} `;
      signaturePathRef.current = newPath;
      return newPath;
    });
    isDownRef.current = true;
    setIsDown(true);
  };

  const move = (e: any) => {
    // Prevent drawing if disabled
    if (isDisabledRef.current || !isDownRef.current) {
      return;
    }
    setSignaturePath((prevPath) => {
      const newPath = `${prevPath}L${getCoords(e)} `;
      signaturePathRef.current = newPath;
      return newPath;
    });
  };

  const up = (e: any) => {
    // Prevent drawing if disabled
    if (isDisabledRef.current || !isDownRef.current) {
      return;
    }
    isDownRef.current = false;
    setIsDown(false);
    onChange?.(signaturePathRef.current);
  };

  // Keep refs in sync with state (needed for touch event handlers that persist)
  useEffect(() => {
    signaturePathRef.current = signaturePath;
    isDownRef.current = isDown;
  }, [signaturePath, isDown]);

  useEffect(() => {
    isDisabledRef.current = isDisabled ?? false;
    // If disabled becomes true, stop any active drawing
    if (isDisabled && isDownRef.current) {
      setIsDown(false);
      isDownRef.current = false;
    }
  }, [isDisabled]);

  // Add non-passive touch event listeners to prevent default behaviors
  useEffect(() => {
    const rect = rectRef.current;
    if (!rect || isDisabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      down(e as any);
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      move(e as any);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      up(e as any);
    };

    // Add non-passive event listeners
    rect.addEventListener('touchstart', handleTouchStart, { passive: false });
    rect.addEventListener('touchmove', handleTouchMove, { passive: false });
    rect.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      rect.removeEventListener('touchstart', handleTouchStart);
      rect.removeEventListener('touchmove', handleTouchMove);
      rect.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDisabled]);

  const clearSignature = () => {
    // Prevent clearing if disabled
    if (isDisabledRef.current) {
      return;
    }
    setIsDown(false);
    isDownRef.current = false;
    setSignaturePath('');
    signaturePathRef.current = '';
    onChange?.('');
  };

  return (
    <Stack gap="md" align="center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{
          border: '2px dashed var(--mantine-color-default-border)',
          touchAction: 'none',
          userSelect: 'none',
          cursor: 'default',
        }}
        width="300"
        height="150"
        viewBox="0 0 300 150"
      >
        <rect
          ref={rectRef}
          id="r"
          width="300"
          height="150"
          style={{ fill: 'transparent' }}
          onMouseDown={isDisabled ? undefined : down}
          onMouseMove={isDisabled ? undefined : move}
          onMouseUp={isDisabled ? undefined : up}
          onMouseOut={isDisabled ? undefined : up}
        />
        <path
          id="p"
          d={signaturePath}
          stroke="navy"
          strokeWidth="2"
          fill="none"
          pointerEvents="none"
        />
      </svg>
      <Button
        size="xs"
        variant="light"
        onClick={clearSignature}
        disabled={isDisabled}
        leftSection={<IconEraser size={16} />}
      >
        {intl.formatMessage({ id: 'clearSignature' })}
      </Button>
    </Stack>
  );
};
